import * as _ from "lodash";
import { addProvider, IProviderDescriptor } from "../ServiceUtil";


import { ResponseInterceptor } from "./http.provider.response.interceptor";
import { IAppConfig } from "./types/IApplicationConfig";

export class RequestInterceptor
{
    /**
     * Holds the unique ID to used to set header "X-Mountain-PlayerId"
     * which should be same on all API requests originating from a tab
     */
    private uniquePlayerId: string = `${Math.random()}-${new Date().getTime()}`;

    /**
     * Holds the language version used to set "Accept-Language" header.
     */
    private acceptLanguage: string = "";

    private accessToken: string = "";

    /**
     * Constructor
     * @param SERVICE_CONFIG is the service layer configuration that provides certain configuration parameters that
     * are needed when formulating API requests
     */
    constructor(private SERVICE_CONFIG: IAppConfig,
        responseInterceptor: ResponseInterceptor)
    {
        this.onRequest = this.onRequest.bind(this); // important in case onRequest is used as a callback
        responseInterceptor.accessTokenSubject.subscribe((token) =>
        {
            this.accessToken = token;
        });
    }

    /**
     * Figure out what type of http method we are dealing with, and then forward to the correct function for handling
     * that method type. Sets defaults on the HTTP config object if they're not set.
     * 
     * Important: This interceptor preserves HTTP-only JWT cookies (X-Access-Token, X-Refresh)
     * by not overriding the headers object completely.
     *
     * @param config : config object that defines the parameters for the request
     * @returns {IHttpRequestConfig}
     */
    public onRequest(config: any): any
    {
        let timeFound: boolean = false;

        // NOTE: Only uncomment for deep debugging as this is called quite a bit.
        // RequestInterceptor.logger.debug(`onRequest( ${config.url} )`);

        // Ensure headers object exists without destroying existing headers
        // This is critical for preserving JWT cookies (X-Access-Token, X-Refresh)
        if (!config.headers)
        {
            config.headers = {};
        }

        // Set CORS headers without overriding existing headers
        // JWT tokens are now in HTTP-only cookies, not Authorization headers
        if (this.accessToken)
        {
            // Only add CORS header, don't override all headers
            config.headers['Access-Control-Allow-Origin'] = '*';
            // Note: JWT tokens (X-Access-Token, X-Refresh) are now HTTP-only cookies
            // No longer using Authorization: Bearer header
            // config.headers.Authorization = `Bearer ${this.accessToken}`;
        }

        // Ensure the base URL is set correctly
        config.baseURL = this.SERVICE_CONFIG.apiEndpoint;

        // Add cache busting support.
        if (!config.params)
        {
            config.params = {};
        }

        // Handle dynamic URL parameters
        Object.keys(config.params)
            .forEach((param) =>
            {
                if (config.params[param] !== "")
                {
                    return; // hands off if the param is not empty;
                }

                switch (param)
                {
                    case "time":
                        config.params[param] = new Date().getTime();
                        timeFound = true;
                        break;
                }
            });

        // Add cache buster if no time param was found
        if (timeFound === false)
        {
            config.params.cacheBuster = new Date().getTime();
        }

        // Set default response type to JSON
        if (!config.responseType)
        {
            config.responseType = "json";
        }

        // Handle raw text responses
        if (config.isRaw)
        {
            config.responseType = "text";
        }

        return config;
    }
}